import { loadStripe, Stripe, StripeCardElement } from '@stripe/stripe-js'
import BillingMethod from 'billing/models/BillingMethod'
import { PaymentRequired } from 'fejl'
import Member from 'iam/models/Member'
import Team from 'iam/models/Team'
import { action, computed, intercept, observable, makeObservable } from 'mobx'
import { Id } from 'types/definitions'
import { extractMessageFromError } from 'utils/error-util'
import { required, validationContext } from 'validx'
import { PaymentMethodEditorViewModel } from './PaymentMethodEditorViewModel'
import { CardPaymentMethodEditorPorts } from './ports'

/**
 * Card payment method editor view model.
 * Single use only.
 */
export default class CardPaymentMethodEditorViewModel extends PaymentMethodEditorViewModel {
  /**
   * Stripe instance.
   */
  stripe: Stripe | null = null

  /**
   * Full name on the card.
   * Default to the current member's display name.
   */
  @observable fullName = this.member.displayName

  /**
   * Address.
   */
  @observable address = ''

  /**
   * Address city.
   */
  @observable city = ''

  /**
   * State, if applicable.
   */
  @observable stateOrTerritory = ''

  /**
   * Country code.
   */
  @observable country = 'US' // how self-absorbed are we

  /**
   * Zip code.
   */
  @observable zipCode = ''

  /**
   * The Stripe card element, needed to create a token.
   */
  card: StripeCardElement | null = null

  /**
   * Validation.
   */
  validation = validationContext(this, {
    fullName: [required('Name is required')],
    address: [required('Address is required')],
    city: [required('City is required')],
    country: [required('Country is required')],
    zipCode: [required('Zip code is required')],
    stateOrTerritory: [
      (ctx) =>
        this.isStateOrTerritoryRelevant
          ? required('State is required')(ctx)
          : true,
    ],
  })

  constructor(
    private member: Member,
    private team: Team | null = null,
    private adapter: CardPaymentMethodEditorPorts
  ) {
    super()
    makeObservable(this)
    // Clears validation on field changes.
    intercept(this, (change) => {
      this.validation.clearErrors(String(change.name))
      return change
    })
  }

  /**
   * Whether the state/territory field is relevant.
   */
  @computed
  get isStateOrTerritoryRelevant() {
    return this.country === 'US'
  }

  /**
   * Sets the state or territory, ensures only 2 characters and uppercase.
   *
   * Because America First. 😤
   *
   * @param value
   */
  @action.bound
  setStateOrTerritory(value: string) {
    const upped = value.toUpperCase()
    if (value && !/[A-Z]/.test(upped)) {
      return
    }

    if (upped.length > 2) {
      return
    }

    this.stateOrTerritory = upped
  }

  /**
   * Sets the country.
   */
  @action.bound
  setCountry(value: string) {
    this.country = value
  }

  /**
   * Loads the Stripe SDK using the key from the Workspace.
   *
   * @protected
   */
  protected async onPrepare(): Promise<void> {
    const key = this.adapter.getStripePublishableKey()
    if (!key)
      throw new Error(
        'There is a missing Billing configuration. Please contact an administrator.'
      )

    this.stripe = await loadStripe(key)
  }

  /**
   * Creates a Token and saves it as a Billing Method.
   *
   * @protected
   */
  protected async onSave(): Promise<BillingMethod | null> {
    if (!this.validation.validate().isValid) {
      return null
    }

    if (!this.stripe || !this.card) {
      throw new TypeError('Stripe was not loaded.')
    }

    const result = await this.stripe.createToken(this.card, {
      name: this.fullName,
      address_line1: this.address,
      address_city: this.city,
      address_country: this.country,
      address_state: this.isStateOrTerritoryRelevant
        ? this.stateOrTerritory
        : '',
      address_zip: this.zipCode,
    })

    if (result.error) {
      this.validation.addErrors({
        card: [result.error.message || 'There was a problem with your card.'],
      })
      return null
    }

    return this.adapter
      .createBillingMethod(this.member, this.team, Id.assert(result.token?.id))
      .catch((err: unknown) => {
        if (this.maybeSniffMeaningfulInfoFromError(err)) {
          return null
        }
        throw err
      })
  }

  /**
   * Listen, I don't need your judgement right now.
   *
   * Makeshift way of figuring out what the server error pertains to,
   * and assign the error to the right field in the validation context.
   *
   * @param err
   * @private
   */
  @action.bound
  private maybeSniffMeaningfulInfoFromError(err: unknown) {
    const message = extractMessageFromError(err)
    if (/zip code/i.test(message)) {
      this.validation.addErrors({ zipCode: [message] })
      return true
    }

    if (/card/i.test(message) || err instanceof PaymentRequired) {
      this.validation.addErrors({ card: [message] })
      return true
    }

    return false
  }
}
